/**
 * Social Security Eligibility Calculator
 *
 * Calculates US Social Security eligibility and estimated benefits for NRIs.
 *
 * Key rules:
 * - Need 40 credits (~10 years of work) for retirement benefits
 * - Max 4 credits per year
 * - Benefits based on highest 35 years of earnings (AIME formula)
 * - Can receive benefits while living in India (USA-India totalization agreement)
 */

import { defaults } from '@/data'

// Constants
const CREDITS_REQUIRED = 40  // 40 credits needed for SS eligibility
const DEFAULT_EXCHANGE_RATE = defaults.profile.exchangeRate

export interface SSEligibilityResult {
  /** Whether user has enough credits (40+) for SS benefits */
  eligible: boolean

  /** Number of credits earned (4 per year worked) */
  creditsEarned: number

  /** Credits needed to reach 40 */
  creditsNeeded: number

  /** Years of additional work needed to qualify */
  yearsToQualify: number

  /** Estimated monthly benefit at full retirement age (USD) */
  estimatedMonthlyBenefit: number

  /** Lifetime value from age 67 to 85 (USD) */
  lifetimeValue: number

  /** Lifetime value formatted (e.g., "$432,000") */
  lifetimeValueFormatted: string

  /** Lifetime value in INR */
  lifetimeValueINR: number

  /** Recommendation based on proximity to eligibility */
  recommendation: 'eligible' | 'stay_longer' | 'return_later' | 'too_far' | null

  /** Human-readable recommendation text */
  recommendationText: string

  /** Whether this opportunity is worth highlighting */
  isHighValueOpportunity: boolean
}

/**
 * Calculate Social Security eligibility and estimated benefits
 *
 * @param yearsWorkedInUSA - Number of years worked in USA
 * @param currentAge - Current age of the user
 * @param annualIncomeUSD - Current/recent annual income in USD
 * @param exchangeRate - USD to INR exchange rate
 * @returns SSEligibilityResult with eligibility status and benefit estimates
 */
export function calculateSSEligibility(
  yearsWorkedInUSA: number | undefined,
  currentAge: number,
  annualIncomeUSD: number,
  exchangeRate: number = DEFAULT_EXCHANGE_RATE
): SSEligibilityResult | null {
  // If years worked not provided, can't calculate
  if (yearsWorkedInUSA === undefined || yearsWorkedInUSA < 0) {
    return null
  }

  // Calculate credits (max 4 per year)
  const creditsEarned = Math.min(yearsWorkedInUSA * 4, 160) // Max 160 (40 years)
  const creditsNeeded = Math.max(0, 40 - creditsEarned)
  const yearsToQualify = Math.ceil(creditsNeeded / 4)
  const eligible = creditsEarned >= 40

  // Estimate monthly benefit using simplified PIA calculation
  const estimatedMonthlyBenefit = eligible
    ? estimateSSBenefit(annualIncomeUSD, yearsWorkedInUSA)
    : estimatePotentialSSBenefit(annualIncomeUSD, yearsWorkedInUSA + yearsToQualify)

  // Calculate lifetime value (age 67 to 85 = 18 years)
  const lifetimeValue = estimatedMonthlyBenefit * 12 * 18
  const lifetimeValueINR = lifetimeValue * exchangeRate

  // Determine recommendation
  let recommendation: SSEligibilityResult['recommendation'] = null
  let recommendationText = ''
  let isHighValueOpportunity = false

  if (eligible) {
    recommendation = 'eligible'
    recommendationText = `You're eligible for Social Security! Estimated lifetime benefit: $${lifetimeValue.toLocaleString()}`
  } else if (yearsToQualify <= 2) {
    recommendation = 'stay_longer'
    recommendationText = `You're only ${yearsToQualify} year${yearsToQualify > 1 ? 's' : ''} away from qualifying. Consider delaying your return to unlock ~$${lifetimeValue.toLocaleString()} in lifetime benefits.`
    isHighValueOpportunity = true
  } else if (yearsToQualify <= 5 && currentAge < 55) {
    recommendation = 'return_later'
    recommendationText = `You need ${yearsToQualify} more years to qualify. You could return to India now and come back later to complete your credits (they don't expire).`
    isHighValueOpportunity = yearsToQualify <= 3
  } else {
    recommendation = 'too_far'
    recommendationText = `You need ${yearsToQualify} more years of US work to qualify for Social Security.`
  }

  return {
    eligible,
    creditsEarned,
    creditsNeeded,
    yearsToQualify,
    estimatedMonthlyBenefit: Math.round(estimatedMonthlyBenefit),
    lifetimeValue: Math.round(lifetimeValue),
    lifetimeValueFormatted: `$${Math.round(lifetimeValue).toLocaleString()}`,
    lifetimeValueINR: Math.round(lifetimeValueINR),
    recommendation,
    recommendationText,
    isHighValueOpportunity
  }
}

/**
 * Estimate Social Security benefit using simplified PIA formula
 *
 * The actual SSA calculation uses:
 * 1. Average Indexed Monthly Earnings (AIME) - highest 35 years
 * 2. Primary Insurance Amount (PIA) - bend point formula
 *
 * This is a simplified estimate for planning purposes.
 */
function estimateSSBenefit(annualIncomeUSD: number, yearsWorked: number): number {
  // Estimate average indexed monthly earnings
  // Assume income represents career average (simplified)
  const monthlyEarnings = annualIncomeUSD / 12

  // Apply SSA earnings cap ($168,600 in 2024)
  const cappedMonthlyEarnings = Math.min(monthlyEarnings, 168600 / 12)

  // 2024 bend points: $1,174 and $7,078
  // PIA formula: 90% of first $1,174 + 32% of next $5,904 + 15% above $7,078
  let pia = 0

  if (cappedMonthlyEarnings <= 1174) {
    pia = cappedMonthlyEarnings * 0.90
  } else if (cappedMonthlyEarnings <= 7078) {
    pia = (1174 * 0.90) + ((cappedMonthlyEarnings - 1174) * 0.32)
  } else {
    pia = (1174 * 0.90) + (5904 * 0.32) + ((cappedMonthlyEarnings - 7078) * 0.15)
  }

  // Adjust for years worked (35-year average)
  // Fewer years = zeros averaged in, reducing benefit
  const yearsFactor = Math.min(yearsWorked / 35, 1)

  // Apply years factor
  const adjustedPia = pia * yearsFactor

  // Calculate credits earned (4 credits per year of work)
  const creditsEarned = yearsWorked * 4

  // Only apply minimum benefit ($800/month) if actually eligible (40+ credits)
  // BUG FIX: Previously showed $800 minimum to users who weren't eligible
  if (creditsEarned >= CREDITS_REQUIRED) {
    return Math.max(adjustedPia, 800)
  }

  // For ineligible users, return the raw calculated amount (no minimum floor)
  return adjustedPia
}

/**
 * Estimate potential benefit if user completes required years
 */
function estimatePotentialSSBenefit(annualIncomeUSD: number, projectedYearsWorked: number): number {
  return estimateSSBenefit(annualIncomeUSD, projectedYearsWorked)
}

/**
 * Format SS eligibility for display
 */
export function formatSSEligibility(result: SSEligibilityResult): {
  status: string
  statusColor: 'green' | 'yellow' | 'red'
  headline: string
  details: string[]
} {
  if (result.eligible) {
    return {
      status: 'Eligible',
      statusColor: 'green',
      headline: `You qualify for ~$${result.estimatedMonthlyBenefit.toLocaleString()}/month`,
      details: [
        `${result.creditsEarned} credits earned (40 required)`,
        `Lifetime value: $${result.lifetimeValue.toLocaleString()}`,
        'Benefits can be received in India'
      ]
    }
  }

  if (result.yearsToQualify <= 2) {
    return {
      status: 'Almost There',
      statusColor: 'yellow',
      headline: `${result.yearsToQualify} more year${result.yearsToQualify > 1 ? 's' : ''} to qualify`,
      details: [
        `${result.creditsEarned}/40 credits earned`,
        `Potential lifetime benefit: $${result.lifetimeValue.toLocaleString()}`,
        'Consider delaying return to India'
      ]
    }
  }

  return {
    status: 'Not Eligible',
    statusColor: 'red',
    headline: `Need ${result.yearsToQualify} more years of work`,
    details: [
      `${result.creditsEarned}/40 credits earned`,
      'Credits don\'t expire - can complete later',
      result.recommendationText
    ]
  }
}
